/**
 * Role-Based Access Control (RBAC) types, permissions, and utilities.
 *
 * Role hierarchy (higher number = more permissions):
 *   SUPER_ADMIN > ADMIN > OPERATOR > ADVERTISER > VIEWER
 *
 * Each role inherits all permissions of roles below it.
 */
import { UserRole } from './types';
// ─── Permission Definitions ─────────────────────────────────────────────────
export var Permission;
(function (Permission) {
    // Organization management
    Permission["MANAGE_ORGANIZATION"] = "manage:organization";
    Permission["MANAGE_USERS"] = "manage:users";
    // Content management
    Permission["UPLOAD_ASSETS"] = "upload:assets";
    Permission["APPROVE_ASSETS"] = "approve:assets";
    Permission["DELETE_ASSETS"] = "delete:assets";
    Permission["VIEW_ALL_ASSETS"] = "view:all_assets";
    // Screen management
    Permission["MANAGE_SCREENS"] = "manage:screens";
    Permission["VIEW_ALL_SCREENS"] = "view:all_screens";
    Permission["REMOTE_ACTIONS"] = "remote:actions";
    // Playlist management
    Permission["MANAGE_PLAYLISTS"] = "manage:playlists";
    Permission["VIEW_ALL_PLAYLISTS"] = "view:all_playlists";
    // Campaign management
    Permission["MANAGE_CAMPAIGNS"] = "manage:campaigns";
    Permission["APPROVE_CAMPAIGNS"] = "approve:campaigns";
    Permission["VIEW_ALL_CAMPAIGNS"] = "view:all_campaigns";
    // Monitoring & operations
    Permission["VIEW_MONITORING"] = "view:monitoring";
    Permission["EMERGENCY_OVERRIDE"] = "emergency:override";
    // Reports
    Permission["VIEW_ALL_REPORTS"] = "view:all_reports";
    Permission["VIEW_OWN_REPORTS"] = "view:own_reports";
})(Permission || (Permission = {}));
// ─── Role-to-Permission Mapping ─────────────────────────────────────────────
const ROLE_PERMISSIONS = {
    [UserRole.SUPER_ADMIN]: new Set(Object.values(Permission)),
    [UserRole.ADMIN]: new Set([
        Permission.MANAGE_ORGANIZATION,
        Permission.MANAGE_USERS,
        Permission.UPLOAD_ASSETS,
        Permission.APPROVE_ASSETS,
        Permission.DELETE_ASSETS,
        Permission.VIEW_ALL_ASSETS,
        Permission.MANAGE_SCREENS,
        Permission.VIEW_ALL_SCREENS,
        Permission.REMOTE_ACTIONS,
        Permission.MANAGE_PLAYLISTS,
        Permission.VIEW_ALL_PLAYLISTS,
        Permission.MANAGE_CAMPAIGNS,
        Permission.APPROVE_CAMPAIGNS,
        Permission.VIEW_ALL_CAMPAIGNS,
        Permission.VIEW_MONITORING,
        Permission.EMERGENCY_OVERRIDE,
        Permission.VIEW_ALL_REPORTS,
        Permission.VIEW_OWN_REPORTS,
    ]),
    [UserRole.OPERATOR]: new Set([
        Permission.UPLOAD_ASSETS,
        Permission.VIEW_ALL_ASSETS,
        Permission.MANAGE_SCREENS,
        Permission.VIEW_ALL_SCREENS,
        Permission.REMOTE_ACTIONS,
        Permission.MANAGE_PLAYLISTS,
        Permission.VIEW_ALL_PLAYLISTS,
        Permission.MANAGE_CAMPAIGNS,
        Permission.VIEW_ALL_CAMPAIGNS,
        Permission.VIEW_MONITORING,
        Permission.EMERGENCY_OVERRIDE,
        Permission.VIEW_ALL_REPORTS,
        Permission.VIEW_OWN_REPORTS,
    ]),
    [UserRole.ADVERTISER]: new Set([
        Permission.UPLOAD_ASSETS,
        Permission.VIEW_ALL_ASSETS,
        Permission.VIEW_ALL_SCREENS,
        Permission.VIEW_ALL_PLAYLISTS,
        Permission.MANAGE_CAMPAIGNS,
        Permission.VIEW_ALL_CAMPAIGNS,
        Permission.VIEW_OWN_REPORTS,
    ]),
    [UserRole.VIEWER]: new Set([
        Permission.VIEW_ALL_ASSETS,
        Permission.VIEW_ALL_SCREENS,
        Permission.VIEW_ALL_PLAYLISTS,
        Permission.VIEW_ALL_CAMPAIGNS,
        Permission.VIEW_MONITORING,
        // VIEW_ALL_REPORTS removed from VIEWER (#55) — a read-only viewer should
        // not have broader report access than an ADVERTISER (who gets VIEW_OWN_REPORTS).
        Permission.VIEW_OWN_REPORTS,
    ]),
};
// ─── Role Hierarchy ─────────────────────────────────────────────────────────
/**
 * Numeric rank for role comparison.
 * Higher number = more privileged.
 */
const ROLE_RANK = {
    [UserRole.VIEWER]: 0,
    [UserRole.ADVERTISER]: 1,
    [UserRole.OPERATOR]: 2,
    [UserRole.ADMIN]: 3,
    [UserRole.SUPER_ADMIN]: 4,
};
/**
 * Roles that can access admin-level routes and operations.
 * These roles can manage users, approve content, and access system settings.
 */
export const ADMIN_ROLES = [
    UserRole.SUPER_ADMIN,
    UserRole.ADMIN,
];
/**
 * Roles that can perform operator-level operations.
 * Includes admin roles plus operators.
 */
export const OPERATOR_ROLES = [
    UserRole.SUPER_ADMIN,
    UserRole.ADMIN,
    UserRole.OPERATOR,
];
// ─── Permission Check Utilities ─────────────────────────────────────────────
/**
 * Check if a role has a specific permission.
 */
export function hasPermission(role, permission) {
    return ROLE_PERMISSIONS[role]?.has(permission) ?? false;
}
/**
 * Check if a role has ALL of the specified permissions.
 */
export function hasAllPermissions(role, permissions) {
    const rolePerms = ROLE_PERMISSIONS[role];
    if (!rolePerms)
        return false;
    return permissions.every((p) => rolePerms.has(p));
}
/**
 * Check if a role has ANY of the specified permissions.
 */
export function hasAnyPermission(role, permissions) {
    const rolePerms = ROLE_PERMISSIONS[role];
    if (!rolePerms)
        return false;
    return permissions.some((p) => rolePerms.has(p));
}
/**
 * Check if role A is at least as privileged as role B.
 */
export function isRoleAtLeast(roleA, roleB) {
    return (ROLE_RANK[roleA] ?? -1) >= (ROLE_RANK[roleB] ?? -1);
}
/**
 * Check if a role is an admin-level role (SUPER_ADMIN or ADMIN).
 */
export function isAdminRole(role) {
    return ADMIN_ROLES.includes(role);
}
/**
 * Check if a role can perform operator-level actions.
 */
export function isOperatorRole(role) {
    return OPERATOR_ROLES.includes(role);
}
/**
 * Get all permissions for a given role.
 */
export function getRolePermissions(role) {
    return Array.from(ROLE_PERMISSIONS[role] ?? []);
}
//# sourceMappingURL=rbac.js.map